import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, Check, ArrowRight, Loader2 } from 'lucide-react';
import BrandMark from '@/components/BrandMark';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PurchaseConfirmationPage() {
  const { user, authHeaders } = useAuth();
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let attempts = 0;
    const fetchLatest = async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${API}/billing/entitlements`, { headers: authHeaders });
        const ents = r.data.entitlements || [];
        // Take the most recently purchased one as the "what you just bought" hero card
        const sorted = ents.filter(e => e.status === 'active').sort((a, b) => (b.purchased_at || '').localeCompare(a.purchased_at || ''));
        if (mounted) {
          setLatest(sorted[0] || null);
          setLoading(false);
        }
      } catch (e) {
        if (mounted) setLoading(false);
      }
    };
    if (user) {
      // Webhooks can lag a few seconds — retry up to 6 times (~12 sec) before giving up
      fetchLatest();
      const id = setInterval(() => {
        if (!mounted || latest || attempts >= 6) { clearInterval(id); return; }
        fetchLatest();
      }, 2000);
      return () => { mounted = false; clearInterval(id); };
    }
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authHeaders]);

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="purchase-confirmation-page">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-white/40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-16">
          <Link to="/" className="flex items-center"><BrandMark size={36} /></Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-fern text-white mb-5 shadow-lg shadow-fern/30">
            <Check className="w-8 h-8" strokeWidth={3} />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-fern-dark tracking-tight">
            Ka pai! You’re in.
          </h1>
          <p className="text-slate-600 text-lg mt-3">
            Your purchase is confirmed. Welcome to NestWise.
          </p>
        </div>

        <Card className="border-0 shadow-[0_4px_24px_-4px_rgba(0,0,0,0.06)] rounded-3xl overflow-hidden mb-6">
          <CardContent className="p-6 sm:p-8">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-slate-400 gap-2">
                <Loader2 className="w-5 h-5 animate-spin" /> Activating your access\u2026
              </div>
            ) : latest ? (
              <>
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-2xl bg-kowhai/30 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-kowhai-dark" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-fern font-semibold mb-1">Just unlocked</p>
                    <p className="font-heading text-xl text-fern-dark">
                      {latest.subject === 'maths' ? 'Pāngarau / Maths' : 'Reo Pākehā / English'}
                      <span className="text-slate-400 text-base"> for </span>
                      <span className="text-moana">{latest.child_name}</span>
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      Lifetime access · No subscription, no expiry
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-2">
                  Your child can now jump into any lesson. Head to the dashboard to set up their planner
                  and start the first kaupapa.
                </p>
              </>
            ) : (
              <p className="text-sm text-slate-600 leading-relaxed">
                Your purchase is being activated. This usually takes a few seconds — if your dashboard
                still says &quot;Locked&quot; in 30 seconds, refresh the page.
              </p>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/dashboard" data-testid="confirmation-dashboard-cta">
            <Button className="bg-fern hover:bg-fern-light text-white rounded-full px-6 gap-2 w-full sm:w-auto">
              Go to Dashboard <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
          <Link to="/lessons" data-testid="confirmation-lessons-cta">
            <Button variant="outline" className="rounded-full px-6 border-fern/30 text-fern hover:bg-fern/5 w-full sm:w-auto">
              Browse Lessons
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
